import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { theme } from '../styles/theme';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Optional: Verify the session on the backend
    // For now, just show success if session_id exists
    if (sessionId) {
      setLoading(false);
    } else {
      setTimeout(() => setLoading(false), 2000);
    }
  }, [sessionId]);

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.typography.fontFamily,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
    },
    content: {
      maxWidth: '600px',
      textAlign: 'center',
      backgroundColor: theme.colors.surface,
      padding: '60px 40px',
      borderRadius: theme.borderRadius.lg,
      border: `2px solid ${theme.colors.borderDark}`,
      boxShadow: theme.shadows.lg,
    },
    icon: {
      fontSize: '4rem',
      marginBottom: '20px',
      color: theme.colors.success || '#22c55e',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: '15px',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    subtitle: {
      fontSize: '1.2rem',
      color: theme.colors.primary,
      marginBottom: '20px',
      fontWeight: '600',
    },
    description: {
      fontSize: '1rem',
      color: theme.colors.textLight,
      lineHeight: '1.6',
      marginBottom: '40px',
    },
    buttonGroup: {
      display: 'flex',
      gap: '15px',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    primaryButton: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.sm,
      padding: '12px 24px',
      fontSize: '0.9rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textDecoration: 'none',
      display: 'inline-block',
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      color: theme.colors.text,
      border: `2px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.sm,
      padding: '12px 24px',
      fontSize: '0.9rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textDecoration: 'none',
      display: 'inline-block',
    },
    loadingSpinner: {
      width: '50px',
      height: '50px',
      border: `4px solid ${theme.colors.border}`,
      borderTop: `4px solid ${theme.colors.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 20px',
    },
    sessionInfo: {
      backgroundColor: theme.colors.backgroundLight,
      padding: '15px',
      borderRadius: theme.borderRadius.md,
      marginBottom: '30px',
      fontSize: '0.8rem',
      color: theme.colors.textLight,
      fontFamily: "'Courier New', monospace",
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.loadingSpinner}></div>
          <h1 style={styles.title}>Processing Payment...</h1>
          <p style={styles.description}>
            Please wait while we confirm your payment and set up your subscription.
          </p>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.icon}>✅</div>
        
        <h1 style={styles.title}>Payment Successful!</h1>
        
        <p style={styles.subtitle}>
          Welcome to ByteBot Pro!
        </p>
        
        <p style={styles.description}>
          Your subscription has been activated successfully. You now have access to all premium features 
          including increased conversation limits, priority support, and advanced customization options.
        </p>

        {sessionId && (
          <div style={styles.sessionInfo}>
            Session ID: {sessionId}
          </div>
        )}

        <div style={styles.buttonGroup}>
          <Link 
            to="/dashboard" 
            style={styles.primaryButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.primaryDark;
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary;
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Go to Dashboard
          </Link>
          
          <Link 
            to="/dashboard/subscription" 
            style={styles.secondaryButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.backgroundLight;
              e.target.style.borderColor = theme.colors.primary;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderColor = theme.colors.border;
            }}
          >
            View Subscription
          </Link>
        </div>
      </div>
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default PaymentSuccess; 